import { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { DataTable, PageHeader, EmptyState, Column } from '@/components/shared';
import TrioButton from '@/trio-ui/Button';
import { FxTransaction, FxVendor, FxCategory } from '@/types/api/finance';

type Props = {
  transactions: FxTransaction[];
  vendors: FxVendor[];
  categories: FxCategory[];
  isLoading: boolean;
  showForm: boolean;
  editingTx: FxTransaction | null;
  onNewTransaction: () => void;
  onEdit: (tx: FxTransaction) => void;
  onDelete: (tx: FxTransaction) => void;
  onSubmit: (data: any) => void;
  onCloseForm: () => void;
  isSubmitting: boolean;
  subscribedVendorIds: Set<string>;
  showSubForm: boolean;
  subFromTx: FxTransaction | null;
  onCreateSubscription: (tx: FxTransaction) => void;
  onSubSubmit: (data: any) => void;
  onCloseSubForm: () => void;
  isSubSubmitting: boolean;
  subError: string | null;
  onBulkDelete: (ids: string[]) => Promise<void>;
};

const formatCurrency = (amount: number, currency = 'USD'): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const FinanceXTransactionsComponent = function ({
  transactions,
  vendors,
  categories,
  isLoading,
  showForm,
  editingTx,
  onNewTransaction,
  onEdit,
  onDelete,
  onSubmit,
  onCloseForm,
  isSubmitting,
  subscribedVendorIds,
  showSubForm,
  subFromTx,
  onCreateSubscription,
  onSubSubmit,
  onCloseSubForm,
  isSubSubmitting,
  subError,
  onBulkDelete,
}: Props): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  const handleBulkDelete = async (): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (
      !window.confirm(
        `Delete ${ids.length} transaction${ids.length !== 1 ? 's' : ''}? This cannot be undone.`
      )
    )
      return;
    setIsDeleting(true);
    try {
      await onBulkDelete(ids);
      setSelectedIds(new Set());
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: Column<FxTransaction>[] = [
    {
      id: 'date',
      label: 'Date',
      width: '100px',
      render: (tx) => (
        <span style={{ color: '#71717A', fontSize: 13 }}>
          {new Date(`${tx.date}T00:00:00`).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      ),
    },
    {
      id: 'description',
      label: 'Description',
      render: (tx) => (
        <span
          style={{
            fontWeight: 500,
            fontSize: 13,
            maxWidth: 240,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {tx.description}
        </span>
      ),
    },
    {
      id: 'vendor',
      label: 'Vendor',
      render: (tx) => (
        <span style={{ fontSize: 13 }}>{tx.vendor?.displayName || tx.vendor?.name || '—'}</span>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      render: (tx) =>
        tx.category ? (
          <Chip
            label={tx.category.name}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              backgroundColor: `${tx.category.color ?? '#6B7280'}20`,
              color: tx.category.color ?? '#6B7280',
            }}
          />
        ) : (
          <span style={{ color: '#C5C5C7' }}>—</span>
        ),
    },
    {
      id: 'amount',
      label: 'Amount',
      align: 'right',
      render: (tx) => (
        <span
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: tx.type === 'income' ? '#10B981' : '#09090B',
          }}
        >
          {tx.type === 'income' ? '+' : '-'}
          {formatCurrency(tx.amount, tx.currency)}
        </span>
      ),
    },
    {
      id: 'source',
      label: 'Source',
      render: (tx) => (
        <Chip
          label={tx.source}
          size="small"
          variant="outlined"
          sx={{ fontSize: 11, textTransform: 'capitalize', borderColor: '#E4E4E7' }}
        />
      ),
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      width: '120px',
      render: (tx) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
          {(() => {
            if (tx.subscriptionId || (tx.vendorId && subscribedVendorIds.has(tx.vendorId))) {
              return (
                <Tooltip title="Linked to subscription">
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: 18, color: '#10B981', mr: 0.5, mt: 0.5 }}
                  />
                </Tooltip>
              );
            }
            if (tx.vendorId) {
              return (
                <Tooltip title="Create subscription">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateSubscription(tx);
                    }}
                    sx={{ color: '#054AD9' }}
                  >
                    <AutorenewIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              );
            }
            return null;
          })()}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(tx);
            }}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(tx);
            }}
            sx={{ color: '#FF5E54' }}
          >
            <DeleteIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Transactions"
        actions={
          <TrioButton variantColor="blue" size="small" onClick={onNewTransaction}>
            <AddIcon sx={{ fontSize: 18, mr: 0.5 }} /> Add Transaction
          </TrioButton>
        }
      />

      <DataTable<FxTransaction>
        columns={columns}
        data={transactions}
        rowKey={(tx) => tx.id}
        isLoading={isLoading}
        searchPlaceholder="Search transactions..."
        searchFields={(tx) =>
          `${tx.description} ${tx.vendor?.name ?? ''} ${tx.category?.name ?? ''}`
        }
        emptyMessage="No transactions yet"
        emptyIcon={ReceiptLongIcon}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        selectionActions={
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={isDeleting ? <CircularProgress size={14} /> : <DeleteOutlineIcon />}
            disabled={isDeleting}
            onClick={handleBulkDelete}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            Delete ({selectedIds.size})
          </Button>
        }
      />

      {/* Add/Edit Transaction Dialog */}
      <Dialog open={showForm} onClose={onCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingTx ? 'Edit Transaction' : 'Add Transaction'}
        </DialogTitle>
        <Formik
          initialValues={{
            date: editingTx?.date ?? new Date().toISOString().split('T')[0],
            description: editingTx?.description ?? '',
            amount: editingTx?.amount ?? '',
            type: editingTx?.type ?? 'expense',
            vendorId: editingTx?.vendorId ?? '',
            categoryId: editingTx?.categoryId ?? '',
            notes: editingTx?.notes ?? '',
          }}
          enableReinitialize
          onSubmit={(values) => {
            onSubmit({
              date: values.date,
              description: values.description,
              amount: Number(values.amount),
              type: values.type,
              vendorId: values.vendorId || undefined,
              categoryId: values.categoryId || undefined,
              notes: values.notes || undefined,
            });
          }}
        >
          {({ values, handleChange }) => (
            <Form>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Field
                    as={TextField}
                    name="date"
                    label="Date"
                    type="date"
                    required
                    size="small"
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                    value={values.date}
                    onChange={handleChange}
                  />
                  <Field
                    as={TextField}
                    name="type"
                    label="Type"
                    select
                    size="small"
                    sx={{ flex: 1 }}
                    value={values.type}
                    onChange={handleChange}
                  >
                    <MenuItem value="expense">Expense</MenuItem>
                    <MenuItem value="income">Income</MenuItem>
                  </Field>
                </Box>
                <Field
                  as={TextField}
                  name="description"
                  label="Description"
                  required
                  fullWidth
                  size="small"
                  value={values.description}
                  onChange={handleChange}
                />
                <Field
                  as={TextField}
                  name="amount"
                  label="Amount"
                  required
                  type="number"
                  fullWidth
                  size="small"
                  value={values.amount}
                  onChange={handleChange}
                />
                <Field
                  as={TextField}
                  name="vendorId"
                  label="Vendor"
                  select
                  fullWidth
                  size="small"
                  value={values.vendorId}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {vendors.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.displayName || v.name}
                    </MenuItem>
                  ))}
                </Field>
                <Field
                  as={TextField}
                  name="categoryId"
                  label="Category"
                  select
                  fullWidth
                  size="small"
                  value={values.categoryId}
                  onChange={handleChange}
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Field>
                <Field
                  as={TextField}
                  name="notes"
                  label="Notes"
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={values.notes}
                  onChange={handleChange}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onCloseForm} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <TrioButton
                  type="submit"
                  variantColor="blue"
                  size="small"
                  disabled={!values.description || !values.amount || isSubmitting}
                >
                  {(() => {
                    if (isSubmitting) return <CircularProgress size={20} />;
                    return editingTx ? 'Update' : 'Create';
                  })()}
                </TrioButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Create Subscription from Transaction Dialog */}
      <Dialog open={showSubForm} onClose={onCloseSubForm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create Subscription from Transaction</DialogTitle>
        <Formik
          initialValues={{
            vendorId: subFromTx?.vendorId || '__new__',
            newVendorName:
              subFromTx?.vendor?.displayName ||
              subFromTx?.vendor?.name ||
              subFromTx?.description ||
              '',
            name: subFromTx?.description ?? '',
            amount: subFromTx?.amount ?? '',
            currency: 'USD',
            billingCycle: 'monthly' as const,
            startDate: subFromTx?.date ?? new Date().toISOString().split('T')[0],
            nextBilling: '',
            seats: '',
            status: 'active' as const,
            owner: '',
            notes: '',
          }}
          enableReinitialize
          onSubmit={(values) => {
            onSubSubmit({
              vendorId: values.vendorId,
              newVendorName: values.newVendorName,
              name: values.name,
              amount: Number(values.amount),
              currency: values.currency,
              billingCycle: values.billingCycle,
              startDate: values.startDate,
              nextBilling: values.nextBilling || undefined,
              seats: values.seats ? Number(values.seats) : undefined,
              status: values.status,
              owner: values.owner || undefined,
              notes: values.notes || undefined,
            });
          }}
        >
          {({ values, handleChange, setFieldValue }) => (
            <Form>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                {subError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {subError}
                  </Alert>
                )}
                <Field
                  as={TextField}
                  name="vendorId"
                  label="Vendor"
                  select
                  required
                  fullWidth
                  size="small"
                  value={values.vendorId}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(e);
                    if (e.target.value !== '__new__') setFieldValue('newVendorName', '');
                  }}
                >
                  <MenuItem value="">Select vendor...</MenuItem>
                  <MenuItem value="__new__" sx={{ fontStyle: 'italic', color: '#054AD9' }}>
                    {values.newVendorName
                      ? `+ ${values.newVendorName} (new)`
                      : '+ Create new vendor'}
                  </MenuItem>
                  {vendors.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.displayName || v.name}
                    </MenuItem>
                  ))}
                </Field>
                {values.vendorId === '__new__' && (
                  <Field
                    as={TextField}
                    name="newVendorName"
                    label="New Vendor Name"
                    required
                    fullWidth
                    size="small"
                    placeholder="e.g. Notion, Figma, AWS"
                    value={values.newVendorName}
                    onChange={handleChange}
                  />
                )}
                <Field
                  as={TextField}
                  name="name"
                  label="Subscription Name"
                  required
                  fullWidth
                  size="small"
                  value={values.name}
                  onChange={handleChange}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Field
                    as={TextField}
                    name="amount"
                    label="Amount"
                    required
                    type="number"
                    size="small"
                    sx={{ flex: 1 }}
                    value={values.amount}
                    onChange={handleChange}
                  />
                  <Field
                    as={TextField}
                    name="billingCycle"
                    label="Billing Cycle"
                    select
                    size="small"
                    sx={{ flex: 1 }}
                    value={values.billingCycle}
                    onChange={handleChange}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="annual">Annual</MenuItem>
                  </Field>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Field
                    as={TextField}
                    name="startDate"
                    label="Start Date"
                    type="date"
                    size="small"
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                    value={values.startDate}
                    onChange={handleChange}
                  />
                  <Field
                    as={TextField}
                    name="nextBilling"
                    label="Next Billing"
                    type="date"
                    size="small"
                    sx={{ flex: 1 }}
                    InputLabelProps={{ shrink: true }}
                    value={values.nextBilling}
                    onChange={handleChange}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Field
                    as={TextField}
                    name="seats"
                    label="Seats"
                    type="number"
                    size="small"
                    sx={{ flex: 1 }}
                    value={values.seats}
                    onChange={handleChange}
                  />
                  <Field
                    as={TextField}
                    name="status"
                    label="Status"
                    select
                    size="small"
                    sx={{ flex: 1 }}
                    value={values.status}
                    onChange={handleChange}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="trial">Trial</MenuItem>
                    <MenuItem value="paused">Paused</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Field>
                </Box>
                <Field
                  as={TextField}
                  name="owner"
                  label="Owner"
                  fullWidth
                  size="small"
                  placeholder="Who manages this?"
                  value={values.owner}
                  onChange={handleChange}
                />
                <Field
                  as={TextField}
                  name="notes"
                  label="Notes"
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={values.notes}
                  onChange={handleChange}
                />
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onCloseSubForm} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
                <TrioButton
                  type="submit"
                  variantColor="blue"
                  size="small"
                  disabled={
                    !values.name ||
                    !values.vendorId ||
                    !values.amount ||
                    (values.vendorId === '__new__' && !values.newVendorName) ||
                    isSubSubmitting
                  }
                >
                  {isSubSubmitting ? <CircularProgress size={20} /> : 'Create Subscription'}
                </TrioButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default FinanceXTransactionsComponent;
